import { useState, useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Menu from "./components/Views/Menu";
import Dashboard from "./components/Views/Dashboard";
import AccountView from "./components/Views/AccountView";
import FeedbackForm from "./components/Views/FeedbackForm";
import Careers from "./components/Lists/Careers";
import CareerEdit from "./components/Edits/CareerEdit";
import PersonSkills from "./components/Lists/PersonSkills";
import SkillTests from "./components/Views/SkillTests";
import SkillCalendar from "./components/Views/SkillCalendar";
import SkillTestRunner from "./components/Views/SkillTestRunner";
import SkillQuestionEdit from "./components/Edits/SkillQuestionEdit";
import EmploymentHistory from "./components/Lists/EmploymentHistory";
import Connections from "./components/Lists/Connections";
import ChatView from "./components/Views/ChatView";
import EmployerEditRoute from "./components/Views/EmployerEditRoute";
import PersonSkillEdit from "./components/Edits/PersonSkillEdit";
import "./style/scss/App.scss";
import Recruit from "./components/Views/Recruit";
import PersonEdit from "./components/Edits/PersonEdit";
import Register from "./components/Views/RegisterForm";
import ResumeView from "./components/Views/ResumeView";
import AgreementSignature from "./components/Edits/AgreementSignature";
import EmployerAdminEdit from "./components/Edits/EmployerAdminEdit";
import Invite from "./components/Edits/InviteEdit";
import EmployerRoleEdit from "./components/Edits/EmployerRoleEdit";
import JobMatchReview from "./components/Views/JobMatchReview";
import ScreeningInviteLanding from "./components/Views/ScreeningInviteLanding";
import CoworkerVerify from "./components/Views/CoworkerVerify";
import JobInterestResponse from "./components/Views/JobInterestResponse";
import PackageItemEdit from "./components/Edits/PackageItemEdit";
import AddEmployerVIP from "./components/Edits/AddEmployerVIP";
import EmployerRecruitEdit from "./components/Edits/EmployerRecruitEdit";
import EmployerView from "./components/Views/EmployerView";

import AdminDashboardView from "./components/Admin/Admin";
import AdminAgreements from "./components/Admin/AdminAgreements";
import AdminSkillEdit from "./components/Admin/AdminSkillEdit";
import AdminEmployerEdit from "./components/Admin/AdminEmployerEdit";
import AdminPackageItemTypes from "./components/Admin/AdminPackageItemTypes";
import LogoutForm from "./components/Controls/LogoutForm";
import RegisterCode from "./static/RegisterCode";
import Blacklist from "./static/blacklist";
import { RequireHiring, RequireSysAdmin } from "./components/Controls/AccessGates";

import MarketingShell from "./marketing/MarketingShell";
import DecisionPage from "./marketing/pages/DecisionPage";
import ForSeekers from "./marketing/pages/ForSeekers";
import ForEmployers from "./marketing/pages/ForEmployers";
import HowItWorks from "./marketing/pages/HowItWorks";
import About from "./marketing/pages/About";
import Faq from "./marketing/pages/Faq";
import Contact from "./marketing/pages/Contact";
import Sms from "./marketing/pages/Sms";
import SmsOptIn from "./marketing/pages/SmsOptIn";
import Privacy from "./marketing/pages/Privacy";
import Terms from "./marketing/pages/Terms";
import SmsJoin from "./marketing/pages/SmsJoin";
import VerifyEmail from "./marketing/pages/VerifyEmail";
import ResetPassword from "./marketing/pages/ResetPassword";
import MustChangePasswordGate from "./components/Controls/MustChangePasswordGate";
import RequiredAgreementsGate from "./components/Controls/RequiredAgreementsGate";

const PUBLIC_PATHS = [
  "/",
  "/login",
  "/logout",
  "/register",
  "/register-code",
  "/blacklist",
  "/about",
  "/faq",
  "/contact",
  "/for-seekers",
  "/for-employers",
  "/how-it-works",
  "/sms",
  "/sms-opt-in",
  "/privacy",
  "/terms",
  "/sms-join",
  "/verify-email",
  "/reset-password",
  "/screening",
  "/skillTest",
  "/coworker-verify",
];

function isPublicPath(pathname) {
  const path = (pathname || "/").toLowerCase();
  return PUBLIC_PATHS.some(
    (p) => path === p || path.startsWith(`${p}?`) || path.startsWith(`${p}/`)
  );
}

function clearAuthSession() {
  window.sessionStorage.removeItem("BearerToken");
  window.sessionStorage.removeItem("UserId");
  window.sessionStorage.removeItem("SessionId");
  window.sessionStorage.removeItem("MustChangePassword");
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!window.sessionStorage.getItem("BearerToken")
  );
  const [mustChangePassword, setMustChangePassword] = useState(
    () => window.sessionStorage.getItem("MustChangePassword") === "1"
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn && !isPublicPath(location.pathname)) {
      clearAuthSession();
      navigate("/");
    }
  }, [isLoggedIn, location.pathname, navigate]);

  useEffect(() => {
    setMustChangePassword(window.sessionStorage.getItem("MustChangePassword") === "1");
  }, [isLoggedIn, location.pathname]);

  return (
    <div className="App">
      {isLoggedIn ? (
        <>
          {mustChangePassword ? (
            <MustChangePasswordGate
              onComplete={() => setMustChangePassword(false)}
            />
          ) : null}
          <Menu updateLogin={setIsLoggedIn} />
          <RequiredAgreementsGate>
          <Routes>
            <Route exact path="/" element={<Dashboard />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/sms" element={<Sms />} />
            <Route path="/sms-opt-in" element={<SmsOptIn />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-join" element={<SmsJoin />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account" element={<AccountView />} />
            <Route path="/feedback" element={<FeedbackForm />} />
            <Route path="/skills" element={<PersonSkills />} />
            <Route path="/skillTests" element={<SkillTests />} />
            <Route path="/skillCalendar" element={<SkillCalendar />} />
            <Route path="/skillTest/:id" element={<SkillTestRunner />} />
            <Route path="/screening/:token" element={<ScreeningInviteLanding />} />
            <Route path="/coworker-verify/:token" element={<CoworkerVerify />} />
            <Route path="/skillQuestions" element={<SkillQuestionEdit />} />
            <Route path="/personSkill/:id?" element={<PersonSkillEdit />} />
            <Route path="/employments" element={<EmploymentHistory />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/chat/:id" element={<ChatView />} />
            <Route path="/employerEdit/:id?" element={<EmployerEditRoute />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:id" element={<CareerEdit />} />
            <Route path="/resume/:id" element={<ResumeView />} />
            <Route path="/recruit" element={<RequireHiring><Recruit /></RequireHiring>} />
            <Route path="/personEdit/:id?" element={<PersonEdit />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-code" element={<RegisterCode />} />
            <Route path="/blacklist" element={<Blacklist />} />
            <Route path="/agreementSignature/:id" element={<AgreementSignature />} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/employerAdminEdit/:id" element={<RequireHiring><EmployerAdminEdit /></RequireHiring>} />
            <Route path="/employerRoleEdit/:id?" element={<RequireHiring><EmployerRoleEdit /></RequireHiring>} />
            <Route path="/jobMatches/:id" element={<RequireHiring><JobMatchReview /></RequireHiring>} />
            <Route path="/jobInterest/:id" element={<JobInterestResponse />} />
            <Route path="/packageItemEdit/:id?" element={<RequireHiring><PackageItemEdit /></RequireHiring>} />
            <Route path="/addEmployerVIP" element={<RequireHiring><AddEmployerVIP /></RequireHiring>} />
            <Route path="/employerRecruitEdit/:id?" element={<RequireHiring><EmployerRecruitEdit /></RequireHiring>} />
            <Route path="/admin" element={<RequireSysAdmin><AdminDashboardView /></RequireSysAdmin>} />
            <Route path="/adminAgreements" element={<RequireSysAdmin><AdminAgreements /></RequireSysAdmin>} />
            <Route path="/adminSkillEdit/:id" element={<RequireSysAdmin><AdminSkillEdit /></RequireSysAdmin>} />
            <Route path="/adminEmployerEdit/:id" element={<RequireSysAdmin><AdminEmployerEdit /></RequireSysAdmin>} />
            <Route path="/adminPackageItemTypes" element={<RequireSysAdmin><AdminPackageItemTypes /></RequireSysAdmin>} />
            <Route path="/employerView/:id" element={<EmployerView />} />
            <Route path="/logout" element={<LogoutForm updateLogin={setIsLoggedIn} />} />
          </Routes>
          </RequiredAgreementsGate>
        </>
      ) : (
        <MarketingShell updateLogin={setIsLoggedIn}>
          <Routes>
            <Route path="/" element={<DecisionPage />} />
            <Route path="/login" element={<DecisionPage />} />
            <Route path="/logout" element={<DecisionPage />} />
            <Route path="/for-seekers" element={<ForSeekers />} />
            <Route path="/for-employers" element={<ForEmployers />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/register" element={<DecisionPage />} />
            <Route path="/register-code" element={<RegisterCode />} />
            <Route path="/blacklist" element={<Blacklist />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/sms" element={<Sms />} />
            <Route path="/sms-opt-in" element={<SmsOptIn />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-join" element={<SmsJoin />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/screening/:token" element={<ScreeningInviteLanding />} />
            <Route path="/coworker-verify/:token" element={<CoworkerVerify />} />
            <Route path="/skillTest/:id" element={<SkillTestRunner />} />
          </Routes>
        </MarketingShell>
      )}
    </div>
  );
}
